import { Fragment, useRef, useState } from 'react';
import type { AccountState, UserView } from '../api.ts';
import { CreateOwner } from './CreateOwner.tsx';
import { OperatorPassword } from './OperatorPassword.tsx';

interface OperatorsProps {
  readonly accountId: string;
  readonly accountState: AccountState;
  readonly users: readonly UserView[];
  /** Reload the account after something here changed it. */
  readonly onChanged: () => void;
}

const COLUMNS = 6;

/**
 * A client's operators, and the two things platform staff may do for them:
 * create an owner, and set a new password for somebody locked out.
 *
 * One reset drawer at a time. Closing it unmounts it, and the one-time password
 * panel inside goes with it — which is the point of keeping the drawer's state
 * in the drawer rather than up here.
 *
 * Closing it — Cancel, or dismissing the one-time password — puts focus back on
 * the row's New password button. The drawer held focus, and without this a
 * keyboard user is dropped at the top of the document once it unmounts.
 */
export const Operators = ({ accountId, accountState, users, onChanged }: OperatorsProps) => {
  const [resetFor, setResetFor] = useState<string | null>(null);
  const triggers = useRef(new Map<string, HTMLButtonElement>());

  const close = (userId: string) => {
    setResetFor(null);
    triggers.current.get(userId)?.focus();
  };

  return (
    <div className="card">
      <div className="card-head">
        <h2>Operators ({users.length})</h2>
      </div>
      <div className="card-body">
        {users.length === 0 ? (
          <p className="note">
            This client has no operators yet, so nobody can sign in to it. Create its first owner
            below.
          </p>
        ) : (
          <div className="scroll">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>State</th>
                  <th>Last signed in</th>
                  <th>
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => {
                  const open = resetFor === user.id;
                  const drawerId = `pf-reset-${user.id}`;
                  return (
                    <Fragment key={user.id}>
                      <tr>
                        <td>{user.displayName}</td>
                        <td>{user.email}</td>
                        <td>{user.role}</td>
                        <td>{user.state}</td>
                        <td>{user.lastSignedInAt?.slice(0, 10) ?? 'never'}</td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-sm"
                            ref={(node) => {
                              if (node === null) triggers.current.delete(user.id);
                              else triggers.current.set(user.id, node);
                            }}
                            aria-expanded={open}
                            aria-controls={open ? drawerId : undefined}
                            aria-label={`Set a new password for ${user.displayName}`}
                            onClick={() => setResetFor(open ? null : user.id)}
                          >
                            New password
                          </button>
                        </td>
                      </tr>
                      {open && (
                        <tr id={drawerId}>
                          <td colSpan={COLUMNS}>
                            <OperatorPassword
                              accountId={accountId}
                              user={user}
                              onReset={onChanged}
                              onClose={() => close(user.id)}
                            />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <CreateOwner accountId={accountId} accountState={accountState} onCreated={onChanged} />
      </div>
    </div>
  );
};
